// Açılış ekranı `public/index.html` içinde durur ve sitenin kendi logosunu
// gösterir; React'in yüklenmesini beklemeden görünür olması gerektiği için
// paketin dışındadır. Kaldırma işi buradadır: ayarlar gelip tema, marka ve
// vitrin hazır olduğunda ekran söner, oyuncu doğrudan kendi sitesini görür.
export const SPLASH_ELEMENT_ID = 'app-splash';
// CSS'teki geçiş süresiyle aynı kalmalı (#app-splash transition: 280ms).
const FADE_MS = 320;

// Şemaya göre site hazır olduktan **sonra** ekranın bekleyeceği süre (ms).
// Bekleme bir alt sınır değil **ek süredir**. Bugün hiçbir şema beklemez:
// Betrun'un 1,5 sn'lik bekleyişi kaldırıldı — Betrun açılış ekranını hiç
// göstermez, site doğrudan açılır (index.html; sistem sahibi kararı,
// 22.10.2026). index.html'deki 8 saniyelik güvenlik ağı bir bekleme
// eklenirse de aşılmamalıdır.
export const SPLASH_HOLD_MS = Object.freeze({});

// Spinlab açılışı sayfa açıldıktan itibaren **en az** bu kadar görünür
// (ürün kararı: 2 sn Winbino açılışı). Ek süre değil alt sınırdır: ayarlar
// 2 sn'den geç gelirse ekran ayarlar gelince kalkar.
export const SPINLAB_SPLASH_MIN_MS = 2000;

export const splashHoldMs = (themeId, now = (typeof performance !== 'undefined' ? performance.now() : 0)) => {
  if (themeId === 'spinlab') return Math.max(0, SPINLAB_SPLASH_MIN_MS - now);
  return SPLASH_HOLD_MS[themeId] || 0;
};

export const SPINLAB_SPLASH_LOGO_KEY = 'app:spinlab-splash-logo-v1';

/**
 * Açılış ekranını Spinlab görünümüne alır (public/index.html
 * `#app-splash.is-spinlab`): ortak marka metni ve logo yerine Spinlab logosu
 * ve kaynak temanın yükleme halkası. Logo bilinmiyorsa yalnızca halka döner;
 * sitenin ortak (diğer şemaların) logosu gösterilmez.
 */
export const styleSpinlabSplash = ({ doc = typeof document !== 'undefined' ? document : null, logo = '' } = {}) => {
  const splash = doc?.getElementById(SPLASH_ELEMENT_ID);
  if (!splash) return false;
  splash.classList.add('is-spinlab');
  const image = doc.getElementById('app-splash-logo');
  if (image && logo && !image.getAttribute('src')) {
    image.src = logo;
    image.hidden = false;
  }
  return true;
};

let hidden = false;

// Spinlab açılışı ayarlar gelince değil, **kabuk boyanınca** kalkar
// (10.10.2026: "site yüklenmeden yüklenmiş gibi gösterilmesin"): tema stil
// dosyası, Spinlab düzeni (logo, menü) ve ilk kare hazır olmalıdır. Kabuk
// tembel yüklendiği için ayar katmanı yalnızca bir güvenlik süresi kurar;
// index.html'deki 8 sn'lik ağ ikisinin de üstündedir.
export const SPINLAB_SPLASH_FALLBACK_MS = 6500;

export const isShellSplashPath = (pathname = typeof window !== 'undefined' ? window.location.pathname : '') => (
  !/^\/admin(?:\/|$)/.test(String(pathname || ''))
);

export const hideSplashWhenPainted = ({ win = typeof window !== 'undefined' ? window : null, holdMs = 0 } = {}) => {
  const raf = win?.requestAnimationFrame ? win.requestAnimationFrame.bind(win) : (fn) => setTimeout(fn, 16);
  // İki kare: ilk kare stilin uygulandığı, ikincisi boyandığı karedir.
  raf(() => raf(() => hideSplash({ holdMs })));
};

export const hideSplash = ({
  doc = typeof document !== 'undefined' ? document : null,
  holdMs = 0,
} = {}) => {
  if (hidden || !doc) return false;
  const splash = doc.getElementById(SPLASH_ELEMENT_ID);
  if (!splash) return false;
  hidden = true;
  const fade = () => {
    splash.classList.add('is-hidden');
    // Geçiş bitince DOM'dan düşer; kalırsa tıklamaları yutmasa da ekran
    // okuyucuya "yükleniyor" demeye devam ederdi.
    setTimeout(() => splash.remove(), FADE_MS);
  };
  const wait = Math.max(0, Number(holdMs) || 0);
  if (wait > 0) setTimeout(fade, wait);
  else fade();
  return true;
};

// Açılış ekranı `public/index.html` içinde son bilinen markayla (localStorage)
// ya da önden istenen ayarlarla doldurulur. İkisi de boş kaldıysa — depolama
// kapalı, ön istek başarısız — ayarlar uygulamaya geldiğinde burada doldurulur:
// ekran bazı şemalarda bir süre daha kalır (bkz. SPLASH_HOLD_MS) ve panelden
// yazılan özel kod logoyu `#app-splash-logo`'dan okur. Zaten dolu ekrana
// dokunulmaz: çizilmiş bir logoyu değiştirmek ekranı titretirdi. `brand`,
// lib/brand.js `cacheBrand` çıktısıdır (logo adresi çözülmüş).
export const SPLASH_LOGO_ID = 'app-splash-logo';
export const SPLASH_TEXT_ID = 'app-splash-text';

const LOGO_PATTERN = /^(?:https?:\/\/|\/)[^\s"'<>]{1,300}$/;

const drawText = (doc, text, brand) => {
  const primary = brand.logo_text_primary || brand.site_name || '';
  if (!text || !primary) return false;
  const accent = doc.createElement('span');
  accent.className = 'app-splash-accent';
  accent.textContent = primary;
  text.appendChild(accent);
  const secondary = brand.logo_text_primary ? brand.logo_text_secondary || '' : '';
  if (secondary) text.appendChild(doc.createTextNode(secondary));
  return true;
};

export const fillSplash = (
  brand = {},
  { doc = typeof document !== 'undefined' ? document : null } = {},
) => {
  if (hidden || !doc || !brand) return false;
  if (!doc.getElementById(SPLASH_ELEMENT_ID)) return false;
  const image = doc.getElementById(SPLASH_LOGO_ID);
  const text = doc.getElementById(SPLASH_TEXT_ID);
  if ((image && !image.hidden) || text?.firstChild) return false;
  const logo = String(brand.loader_logo_url || brand.logo_image_url || '').trim();
  if (image && LOGO_PATTERN.test(logo)) {
    image.onerror = () => {
      image.hidden = true;
      image.removeAttribute('src');
      drawText(doc, text, brand);
    };
    image.src = logo;
    image.hidden = false;
    return true;
  }
  return drawText(doc, text, brand);
};

// Testler için: modül durumu sıfırlanır.
export const resetSplashState = () => { hidden = false; };

export default hideSplash;
